/**
 * Clear Persisted Stores Utility
 *
 * Clears all Zustand persisted store data from localStorage
 * Should be called during logout to prevent stale data on next login
 *
 * Note: All stores now have environment prefixes (development-, staging-, etc.)
 * This utility automatically handles the environment prefix.
 */

import { envStorage } from "@/lib/storage/envIsolatedStorage";

/**
 * List of all persisted store keys in localStorage
 * Update this list when adding new persisted stores
 *
 * Note: user-additional-store is intentionally EXCLUDED from this list.
 * We preserve the user's organization/clinic selection across logout/login
 * to improve UX. If you need to clear it (e.g., switching users), use
 * clearAllIncludingAdditionalInfo() instead.
 */
const PERSISTED_STORE_KEYS = [
  // "user-additional-store", // ← EXCLUDED: preserve across logout
  "user-store",
  "clinic-store",
  "patient-store",
  "visit-storage",
  "vital-signs-storage",
  "echo-consultation-storage",
  "echo-common-storage",
  "MasterDataStore",
  "user-category-general-config-store",
  "LegacyPatientStore",
  "doctor-store",
  // Note: Theme stores are intentionally NOT included
  // "echo-theme-storage",
  // "echo-theme-store",
] as const;

/**
 * List of stores to clear when switching users
 */
const ALL_STORE_KEYS = [
  "user-additional-store", // Include this when switching users
  ...PERSISTED_STORE_KEYS,
] as const;

/**
 * Clear persisted store data from localStorage
 *
 * This function removes application state data but preserves:
 * - Theme settings (user preferences)
 * - Auth tokens (handled separately by AuthConsumer)
 * - AdditionalInfo (organization/clinic selection) to improve UX
 *
 * For same user re-login, this preserves their work environment selection.
 * For switching users, use clearAllIncludingAdditionalInfo() instead.
 *
 * @example
 * ```typescript
 * // In normal logout handler
 * clearPersistedStores();
 * resetAllStores();
 * ```
 */
export const clearPersistedStores = (): void => {
  PERSISTED_STORE_KEYS.forEach((key) => {
    try {
      // envStorage automatically adds environment prefix
      envStorage.removeItem(key);
    } catch (error) {
      console.warn(`[clearPersistedStores] Failed to remove ${key}:`, error);
    }
  });

  console.log("[clearPersistedStores] Persisted stores cleared (AdditionalInfo preserved)");
};

/**
 * Clear ALL persisted stores including AdditionalInfo
 *
 * Use this when switching users or when you need to completely reset
 * the application state.
 *
 * @example
 * ```typescript
 * // When switching to a different user account
 * clearAllIncludingAdditionalInfo();
 * resetAllStores();
 * ```
 */
export const clearAllIncludingAdditionalInfo = (): void => {
  ALL_STORE_KEYS.forEach((key) => {
    try {
      // envStorage automatically adds environment prefix
      envStorage.removeItem(key);
    } catch (error) {
      console.warn(`[clearAllIncludingAdditionalInfo] Failed to remove ${key}:`, error);
    }
  });

  console.log("[clearAllIncludingAdditionalInfo] All stores cleared including AdditionalInfo");
};

/**
 * Clear specific persisted stores by keys
 *
 * @param keys - Array of store keys to clear
 * @example
 * ```typescript
 * clearSpecificStores(['user-store', 'clinic-store']);
 * ```
 */
export const clearSpecificStores = (keys: readonly string[]): void => {
  keys.forEach((key) => {
    try {
      // envStorage automatically adds environment prefix
      envStorage.removeItem(key);
    } catch (error) {
      console.warn(`[clearSpecificStores] Failed to remove ${key}:`, error);
    }
  });
};

/**
 * Get list of all persisted store keys
 * Useful for debugging or auditing purposes
 */
export const getPersistedStoreKeys = (): readonly string[] => {
  return PERSISTED_STORE_KEYS;
};
